import type { Auth0ContextInterface, IdToken } from '@auth0/auth0-react';

// The backend verifies this ID token when creating a session, so a cached one
// past its expiry (e.g. when re-minting a day-old session) is refreshed first.
export async function getFreshIdTokenClaims(
  auth0: Pick<Auth0ContextInterface, 'getIdTokenClaims' | 'getAccessTokenSilently'>
): Promise<IdToken | undefined> {
  let claims = await auth0.getIdTokenClaims();
  if (!claims?.exp || claims.exp * 1000 < Date.now() + 60_000) {
    await auth0.getAccessTokenSilently({ cacheMode: 'off', timeoutInSeconds: 10 });
    claims = await auth0.getIdTokenClaims();
  }
  return claims;
}

// When the cached Auth0 login can't produce a valid ID token (e.g. its refresh
// token expired), sign in again rather than sending a login the backend rejects.
// Returns false if a re-login was already tried in the last two minutes, so a
// fresh login that still fails surfaces an error instead of looping.
export async function reauthenticateOnce(
  loginWithRedirect: Auth0ContextInterface['loginWithRedirect']
): Promise<boolean> {
  const lastReauth = Number(sessionStorage.getItem('auth0_reauth_at') || 0);
  if (Date.now() - lastReauth < 120_000) return false;

  sessionStorage.setItem('auth0_reauth_at', String(Date.now()));
  await loginWithRedirect({ appState: { returnTo: window.location.pathname + window.location.search } });
  return true;
}
